import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, XIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash/fp';
import {
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/types';
import { AxisInterval, BaseAxisProps } from 'recharts/types/util/types';
import styled, { ThemeContext } from 'styled-components';

import ChartLayout, { ChartTooltipHeader } from 'dashboard/layouts/ChartLayout';
import { Period } from 'dashboard/lib/unixTime';
import { formatChartDate, formatNumber, formatNumberLarge } from 'dashboard/lib/utils/formatters';
import { CheckStatus } from 'dashboard/models/check';
import {
  VerificationCheckLineChartDatum,
  VerificationCheckV2Datum,
  VerificationCheckV2Metrics,
  VerificationCheckV2TypeDatum,
} from 'dashboard/models/metrics';
import { Z_INDICES } from 'dashboard/styles/variables';
import ChecksExpandedLegend from 'dashboard/views/DashboardVerificationAnalyticsChecksV2View/ChecksExpandedLegend';
import Button from 'design-system/components/Button';
import { Heading, Label } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';
import { assertNever } from 'lib/utils/types';
import { CheckName } from 'lib/verificationCheck';

const DEFAULT_COLORS: string[] = [
  Color.CoreTeal900,
  Color.CorePurple800,
  Color.CoreGreen900,
  Color.CoreOrange800,
  Color.CoreYellow900,
  Color.CoreRed900,
  Color.CoreBlue800,
  Color.CoreSlate700,
];

export interface RenderLabelProps {
  x: number;
  y: number;
  fill: string;
  value: unknown;
  index: number;
  width: number;
  height: number;
}

const ChartTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: auto;

  box-shadow: ${Elevation.Mid};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${Spacing[2]} ${Spacing[3]};
  z-index: ${Z_INDICES.toast};
  background-color: ${({ theme }) => theme.color.primary.white};
`;

const ObservationsLabel = styled.div<{ hovered?: boolean }>`
  color: ${({ hovered }) => (hovered ? Color.CorePurple700 : Color.CorePurple600)};
  text-decoration: ${({ hovered }) => (hovered ? 'underline' : undefined)};
  cursor: pointer;
`;

const StyledButton = styled(Button)`
  margin-left: auto;
  position: relative;
  background-color: white;
`;

interface ActivatableProps {
  active?: boolean;
  hasActive?: boolean;
  hovered?: boolean;
}

interface TooltipPayloadDatum {
  dataKey: CheckName;
  value: number;
  color: string;
  fill: string;
}

export const ChartTooltipLabelContainer = styled.div<ActivatableProps>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} ${Spacing[4]} ${Spacing[1]} ${Spacing[2]};
  width: 100%;
  min-width: 260px;

  .title {
    justify-self: start;
  }

  justify-self: end;

  background-color: ${({ hovered }) => (hovered ? Color.CorePurple200 : undefined)};

  transition-duration: ${TransitionDuration.Fast};
  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ active, hasActive }) => (hasActive ? (active ? 1 : 0.3) : 1)};
`;

export const ChartTooltipLabelDropdown = styled.div<ActivatableProps>`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} ${Spacing[4]} ${Spacing[1]} ${Spacing[2]};
  width: 100%;
  min-width: 260px;

  .status {
    justify-self: start;
    padding-left: ${Spacing[4]};
    color: ${({ theme }) => theme.color.text.secondary};
  }

  .link {
    justify-self: end;
    padding-right: ${Spacing[5]};
  }
`;

export interface ChecksLineChartV2Props {
  data: VerificationCheckLineChartDatum[];
  rawChecksData: VerificationCheckV2TypeDatum[];
  lineKeys: CheckName[];
  lineGroups: Record<string, Partial<Record<CheckName, string>>>;
  requiredChecks: Record<string, string>;
  unrequiredChecks: Record<string, string>;
  summaryData: Record<string, VerificationCheckV2Metrics>;
  createTooltipHeader: (label: number) => string | undefined;
  legendFormatter?: (value: string) => string;
  renderTooltip?: (hoveredKey: string) => ReactNode;
  legendHeight?: number | 'auto';
  keyFormatter?: (value: string) => string;
  renderBarLabel?: (props: RenderLabelProps, idx: number) => ReactNode;
  chartHeight?: number;
  interval: AxisInterval;
  onDeepLink: (
    check: string,
    label: number,
    checkNameMap: Record<string, CheckName>,
    status: CheckStatus,
    groupByEnabled: boolean,
  ) => void;
  checkNameMap: Record<string, CheckName>;
  groupedByInquiries: boolean;

  // x-axis options
  xAxisHeight?: number;
  xAxisTextAnchor?: string;
  xAxisAngle?: number;
  xDataKey: string | ((data: unknown) => string);
  xFormatter?: (value: any) => string | undefined;
  xAxisTick?: BaseAxisProps['tick'];

  // y-axis options
  yAxisTicks?: (number | string)[];

  barIsAnimationActive?: boolean;
  initialFilteredLegendKeys?: string[];
  chartPeriod?: Period;
}

const DEFAULT_DIMENSION_MARGIN = 16;

const DIMENSIONS = {
  height: 290,
  margin: {
    top: DEFAULT_DIMENSION_MARGIN * 2,
    left: -DEFAULT_DIMENSION_MARGIN,
    right: DEFAULT_DIMENSION_MARGIN,
  },
};

const StaticTooltipContainer = styled.div<{
  x: number;
  y: number;
}>`
  position: absolute;
  z-index: ${Z_INDICES.tooltip};
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  box-shadow: ${Elevation.Mid};

  transition:
    height ${TransitionDuration.Medium},
    width ${TransitionDuration.Medium};
`;

const HeaderContainer = styled.div`
  padding: ${Spacing[2]};
  padding-top: ${Spacing[1]};
  width: 100%;
  float: left;
  display: flex;
  flex-direction: column;
`;

const StyledCaretDownIcon = styled(CaretDownIcon)<{
  expanded: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0')});
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-left: ${Spacing[2]};
`;

const ChartHeader: FC<PropsWithChildren<object>> = ({ children }) => (
  <HeaderContainer>
    <Heading size='sm' style={{ display: 'flex' }}>
      {children}
    </Heading>
  </HeaderContainer>
);

export const ChecksLineChartV2 = ({
  data,
  rawChecksData,
  summaryData,
  lineKeys,
  requiredChecks,
  unrequiredChecks,
  createTooltipHeader,
  legendFormatter = startCase,
  renderTooltip,
  yAxisTicks,
  barIsAnimationActive,
  initialFilteredLegendKeys,
  xFormatter = formatChartDate,
  onDeepLink,
  checkNameMap,
  groupedByInquiries,
  interval,
}: ChecksLineChartV2Props) => {
  const theme = useContext(ThemeContext);

  const [filteredLegendKeys, setFilteredLegendKeys] = useState(
    initialFilteredLegendKeys == null ? lineKeys : [...initialFilteredLegendKeys],
  );
  const tooltipRef = useRef<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const staticContainerRef = useRef<HTMLDivElement>();

  const [isHovered, { enable: activateStaticToolTip, disable: deactivateStaticToolTip }] =
    useToggle(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipPayload, setTooltipPayload] = useState<TooltipPayloadDatum[] | undefined>(
    undefined,
  );
  const [tooltipLabel, setTooltipLabel] = useState(null);
  const [yScrollPosition, setYScrollPosition] = useState(0);

  // Create a map of Colors to pass through to the Legend
  const colorsMap: Partial<Record<CheckName, string>> = {};
  lineKeys.forEach((key, idx) => {
    colorsMap[key] = DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
  });

  // When a new load happens (new information given to the chart, we'll get rid of the tooltip
  useEffect(() => {
    deactivateStaticToolTip();
    setDropdownKey(null);
  }, [rawChecksData]);

  // Helper function to add a line to the tooltip payload
  const addValueToStaticTooltip = (checkName: CheckName, label: number) => {
    if (tooltipPayload && tooltipLabel) {
      const checksDatum: VerificationCheckLineChartDatum | undefined = data.find(
        (datum) => datum.created_timestamp === label,
      );

      // Must sort based off of the lineKeys to ensure it remains consistent with tooltip
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const newPayload: TooltipPayloadDatum[] = [
        ...tooltipPayload,
        {
          dataKey: checkName,
          color: colorsMap[checkName],
          fill: colorsMap[checkName],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value: checksDatum[checkName],
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ].sort(function (a?: TooltipPayloadDatum, b?: TooltipPayloadDatum) {
        return (
          lineKeys.findIndex((key) => key === a?.dataKey) -
          lineKeys.findIndex((key) => key === b?.dataKey)
        );
      });
      setTooltipPayload(newPayload);
    }
  };

  const removeValueFromStaticTooltip = (checkName: CheckName) => {
    if (tooltipPayload && tooltipLabel) {
      setTooltipPayload(
        tooltipPayload.filter((datum: TooltipPayloadDatum) => datum.dataKey !== checkName),
      );
    }
  };

  // Helper function to find how many inquiries with the given status are present
  // in this given datum
  const findObservationsWithStatus = (checkName: string, label: number, status: CheckStatus) => {
    // Get the actual check name + find the datum that we want
    const realCheckName = checkNameMap[checkName];
    const isRequired = Object.keys(requiredChecks).includes(checkName);
    const checksData = rawChecksData.find(
      (datum) => datum.check_required === isRequired && datum.name === realCheckName,
    );

    // Search through the raw data to get the number of observations for the given inquiries
    if (checksData == null) {
      return 0;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const checksDatum: VerificationCheckV2Datum = checksData.data.find(
      (datum) => datum.created_timestamp === label,
    );
    if (checksDatum == null) {
      return 0;
    }
    switch (status) {
      case CheckStatus.Passed:
        return checksDatum.pass_count;
      case CheckStatus.Failed:
        return checksDatum.failure_count;
      case CheckStatus.NotApplicable:
        return checksDatum.not_applicable_count;
      default:
        return assertNever(status);
    }
  };

  // Deactivate the tooltip and set everything back
  const deactivateTooltipAndReset = () => {
    deactivateStaticToolTip();
    setClickedKey(null);
    setHoveredKey(null);
    setDropdownKey(null);
  };

  // When we activate the tip, we'll need to flip the toggle and save the position
  // as well as the X Axis Label (Date)
  const toggleStaticToolTip = (event: CategoricalChartState) => {
    // If it's already enabled, disable it
    if (isHovered) {
      deactivateTooltipAndReset();
      return;
    }

    if (event == null) {
      return;
    }

    // Ensure that we've clicked at a point where we have a live Tooltip visible
    if (event.activePayload != null && tooltipRef.current != null) {
      activateStaticToolTip();
      setTooltipPayload(event.activePayload);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setTooltipLabel(event.activeLabel);
      // We move the tooltip to a set portion away from the current cursor. This is so that we
      // ensure the user understands that the sticky tooltip has been employed
      const toolTipPositions = tooltipRef.current.getBoundingClientRect();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const containerPositions = containerRef.current.current.getBoundingClientRect();

      // Gotta adjust for some padding
      setTooltipPosition({
        x: toolTipPositions.x - containerPositions.x + 16,
        // TODO: [jonny] Revisit this
        // Adjust the height of the tooltip and then account for the top padding
        y: toolTipPositions.y - containerPositions.y - 24 * filteredLegendKeys.length + 40,
      });
    }
  };

  const StyledChartTooltipLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
    color: ${({ theme }) => theme.color.text.primary};
    cursor: pointer;
    font-weight: ${FontWeight.Regular};
    justify-self: end;
  `;

  // handle a reload of the initialFilteredLegendKeys
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFilteredLegendKeys(initialFilteredLegendKeys);
  }, [initialFilteredLegendKeys]);

  const [hoveredKey, setHoveredKey] = useState(null);
  const [dropdownKey, setDropdownKey] = useState(null);
  const [clickedKey, setClickedKey] = useState(null);
  const [underlineKey, setUnderlineKey] = useState(null);
  const [underlineStatus, setUnderlineStatus] = useState(null);

  // When there is re-rendering, we'll want to scroll to exactly where the Y position of the scroll was
  useLayoutEffect(() => {
    staticContainerRef.current?.scrollTo(0, yScrollPosition);
  }, [yScrollPosition, hoveredKey, clickedKey, dropdownKey, underlineKey, underlineStatus]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const TooltipContent: FC<
    ComponentProps<typeof Tooltip> & {
      active?: boolean;
      label?: string;
      payload?: any[];
      hoveredKey: string | null;
      createHeader: (label: number) => string;
      legendFormatter: (value: string) => string;
      keyToUnit: (value: string) => string;
      tooltipRef: RefObject<HTMLDivElement>;
    }
  > = ({
    active,
    payload,
    label,
    hoveredKey,
    legendFormatter,
    createHeader,
    keyToUnit,
    tooltipRef,
  }) => {
    if (!active || payload == null) {
      return null;
    }
    const countLabels = payload
      .slice()
      .map(({ color, dataKey, value }) =>
        typeof value === 'number' && value > 0 ? (
          <ChartTooltipLabelContainer
            ref={tooltipRef}
            key={dataKey}
            active={true}
            hasActive={hoveredKey != null}
          >
            <StyledChartTooltipLabel className='title'>
              <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
            </StyledChartTooltipLabel>
            <StyledChartTooltipLabel>
              {formatNumber(+value)}
              {keyToUnit(dataKey as string)}
            </StyledChartTooltipLabel>
          </ChartTooltipLabelContainer>
        ) : null,
      )
      .filter(Boolean);

    return (
      countLabels.length > 0 && (
        <ChartTooltipContainer id='checks-tooltip'>
          <ChartTooltipHeader>{createHeader(label)}</ChartTooltipHeader>
          {countLabels}
        </ChartTooltipContainer>
      )
    );
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const StaticToolTip: FC<{
    active?: boolean;
    label?: number;
    payload?: any[];
    hoveredKey: string | null;
    createHeader: (label: number) => string;
    legendFormatter: (value: string) => string;
    keyToUnit: (value: string) => string;
    tooltipRef: RefObject<HTMLDivElement>;
  }> = ({ active, payload, label, hoveredKey, legendFormatter, createHeader, keyToUnit }) => {
    if (!active || payload == null) {
      return null;
    }

    const countLabels = payload
      .slice()
      .map(({ color, dataKey, value }) =>
        value > 0 ? (
          <>
            <ChartTooltipLabelContainer
              key={dataKey}
              active={true}
              hovered={
                (dataKey === hoveredKey && dropdownKey !== dataKey && clickedKey == null) ||
                clickedKey === dataKey
              }
              hasActive={hoveredKey != null}
              onClick={() => {
                // When it's already clicked, flip it back up
                setDropdownKey(dropdownKey === dataKey ? null : dataKey);
                setClickedKey(clickedKey === dataKey ? null : dataKey);
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                setYScrollPosition(staticContainerRef.current?.scrollTop);
              }}
              onMouseEnter={() => {
                setHoveredKey(dataKey);
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                setYScrollPosition(staticContainerRef.current?.scrollTop);
              }}
            >
              <StyledChartTooltipLabel className='title'>
                <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
              </StyledChartTooltipLabel>
              <StyledChartTooltipLabel>
                {formatNumber(+value)}
                {keyToUnit(dataKey as string)}
                <StyledCaretDownIcon expanded={clickedKey === dataKey} />
              </StyledChartTooltipLabel>
            </ChartTooltipLabelContainer>

            {dataKey === dropdownKey && (
              <ChartTooltipLabelDropdown>
                <StyledChartTooltipLabel className='status'>Passed</StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='status'>
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  {findObservationsWithStatus(dataKey, label, CheckStatus.Passed)}
                </StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='link'>
                  <ObservationsLabel
                    onClick={() =>
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      handleDeepLink(dataKey, label, CheckStatus.Passed, groupedByInquiries)
                    }
                    onMouseEnter={() => {
                      setUnderlineKey(dataKey);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setUnderlineStatus(CheckStatus.Passed);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    onMouseLeave={() => {
                      setUnderlineStatus(null);
                      setUnderlineKey(null);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    hovered={underlineKey === dataKey && CheckStatus.Passed === underlineStatus}
                  >
                    View
                  </ObservationsLabel>
                </StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='status'>Failed</StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='status'>
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  {findObservationsWithStatus(dataKey, label, CheckStatus.Failed)}
                </StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='link'>
                  <ObservationsLabel
                    onClick={() =>
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      handleDeepLink(dataKey, label, CheckStatus.Failed, groupedByInquiries)
                    }
                    onMouseEnter={() => {
                      setUnderlineKey(dataKey);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setUnderlineStatus(CheckStatus.Failed);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    onMouseLeave={() => {
                      setUnderlineStatus(null);
                      setUnderlineKey(null);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    hovered={underlineKey === dataKey && CheckStatus.Failed === underlineStatus}
                  >
                    View
                  </ObservationsLabel>
                </StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='status'>N/A</StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='status'>
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  {findObservationsWithStatus(dataKey, label, CheckStatus.NotApplicable)}
                </StyledChartTooltipLabel>
                <StyledChartTooltipLabel className='link'>
                  <ObservationsLabel
                    onClick={() =>
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      handleDeepLink(dataKey, label, CheckStatus.NotApplicable, groupedByInquiries)
                    }
                    onMouseEnter={() => {
                      setUnderlineKey(dataKey);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setUnderlineStatus(CheckStatus.NotApplicable);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    onMouseLeave={() => {
                      setUnderlineStatus(null);
                      setUnderlineKey(null);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setYScrollPosition(staticContainerRef.current?.scrollTop);
                    }}
                    hovered={
                      underlineKey === dataKey && CheckStatus.NotApplicable === underlineStatus
                    }
                  >
                    View
                  </ObservationsLabel>
                </StyledChartTooltipLabel>
              </ChartTooltipLabelDropdown>
            )}
          </>
        ) : null,
      )
      .filter(Boolean);

    return (
      countLabels.length > 0 && (
        <StaticTooltipContainer
          onMouseLeave={() => setHoveredKey(null)}
          y={tooltipPosition.y}
          x={tooltipPosition.x}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <ChartTooltipContainer ref={staticContainerRef}>
            <ChartHeader>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {createHeader(label)}{' '}
              <StyledButton
                size='sm'
                variant='basic'
                width='icon'
                onClick={() => {
                  deactivateTooltipAndReset();
                }}
              >
                <XIcon />
              </StyledButton>
            </ChartHeader>
            {countLabels}
          </ChartTooltipContainer>
        </StaticTooltipContainer>
      )
    );
  };

  const handleExpandedLegendClick = (dataKey: CheckName) => {
    // When it's in the tooltip, remove it, when it's not, add it
    if (filteredLegendKeys.includes(dataKey)) {
      setFilteredLegendKeys(filteredLegendKeys.filter((s) => s !== dataKey));
      removeValueFromStaticTooltip(dataKey);
    } else {
      setFilteredLegendKeys(filteredLegendKeys.concat(dataKey));
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      addValueToStaticTooltip(dataKey, tooltipLabel);
    }
  };

  const handleFillExpandedLegend = (dataKeys: string[]) => {
    setFilteredLegendKeys(dataKeys);
    deactivateStaticToolTip();
  };

  const tooltipContent = renderTooltip ? (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => renderTooltip(hoveredKey)
  ) : (
    <TooltipContent
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createHeader={createTooltipHeader}
      hoveredKey={hoveredKey}
      legendFormatter={legendFormatter}
      keyToUnit={(key) => (lineKeys.includes(key as CheckName) ? '%' : '')}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      tooltipRef={tooltipRef}
    />
  );

  const handleDeepLink = useCallback(
    (check: CheckName, label: number, status: CheckStatus, groupByEnabled: boolean) =>
      onDeepLink(check, label, checkNameMap, status, groupByEnabled),
    [onDeepLink, data, filteredLegendKeys],
  );

  return (
    <ChartLayout id='sample-layout'>
      {isHovered && (
        <StaticToolTip
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          createHeader={createTooltipHeader}
          hoveredKey={hoveredKey}
          payload={tooltipPayload}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          label={tooltipLabel}
          legendFormatter={legendFormatter}
          keyToUnit={(key) => (lineKeys.includes(key as CheckName) ? '%' : '')}
          active={true}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipRef={tooltipRef}
        />
      )}
      <ResponsiveContainer height={DIMENSIONS.height} ref={containerRef}>
        <LineChart
          data={data}
          margin={DIMENSIONS.margin}
          onClick={toggleStaticToolTip}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            // Note we are using categorical x axis; this is because D3 doesn't handle time series bar charts well
            dataKey='created_timestamp'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            interval={interval}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            tickFormatter={xFormatter}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
          />
          <YAxis
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={formatNumberLarge}
            unit='%'
            ticks={yAxisTicks}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          {!isHovered && (
            <Tooltip
              isAnimationActive={false}
              active={!isHovered}
              content={tooltipContent}
              allowEscapeViewBox={{
                x: false,
                y: true,
              }}
            />
          )}
          {lineKeys.map((key, idx) => {
            const fillColor = DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
            return (
              <Line
                key={key}
                dataKey={key}
                isAnimationActive={barIsAnimationActive != null ? barIsAnimationActive : true}
                // This element doesn't take ID and the library types are wrong
                // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                fill={fillColor}
                fillOpacity={hoveredKey == null || hoveredKey === key ? 1 : 0}
                stroke={fillColor}
                dot={false}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                strokeWidth={hoveredKey === key ? 2 : null}
                hide={!filteredLegendKeys.includes(key) && hoveredKey !== key}
                style={{
                  transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
                }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
      <ChecksExpandedLegend
        dataTest='checks-checks-table'
        canSort={true}
        summaryData={summaryData}
        requiredChecks={requiredChecks}
        unrequiredChecks={unrequiredChecks}
        colorsMap={colorsMap}
        activeKeys={filteredLegendKeys}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        hoveredKey={hoveredKey}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setHoveredKey={setHoveredKey}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleLegendClick={handleExpandedLegendClick}
        handleFillLegend={handleFillExpandedLegend}
        // checkGroups={}
      />
    </ChartLayout>
  );
};

export default ChecksLineChartV2;
